<template>
  <div>
    <Header
      :history="history"
      :data="drawerData"
      :action="action"
      :paymentMethod="paymentMethod"
      @goBack="goBack"
      @toggleAction="toggleAction"
      @close="close"
    />

    <transition name="fade">
      <div v-if="isSuccess" :style="`height: ${contentHeight}px`">
        <div v-if="hasRole('billing', 'ROLE_GET_FINANCIAL_ACCOUNT_PAYMENT_METHOD')">
          <Preview
            v-if="action !== 'edit'"
            :paymentMethod="paymentMethod"
            :data="paymentMethodData"
            :confirm="confirmChangePaymentMethod"
            @change="change"
            @close="close"
            @toggleAction="toggleAction"
          />

          <Edit
            v-if="action === 'edit'"
            :data="paymentMethodData"
            @toggleAction="toggleAction"
            @close="close"
          />
        </div>

        <EmptyState
          v-else
          image="employee"
          :imageSize="120"
          class="px-4"
        >
          <span class="state__text">{{ $t('Views.ReportsPage.withoutPermission') }}</span>
        </EmptyState>
      </div>
    </transition>

    <div
      v-if="isLoading"
      class="progress__wrapper text-xs-center py-5"
      :style="`height: ${contentHeight}px`"
    >
      <div class="progress">
        <v-progress-circular indeterminate color="primary" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { hasRole } from 'utils/functions'
import { calcContentHeight, catchError } from 'corp-front-vue-commons/src/utils/functions'
import Header from './Header'
import Preview from './Preview'
import Edit from './Edit'
import EmptyState from 'components/EmptyState'

export default {
  name: 'PaymentMethod',
  props: {
    data: {
      type: Object,
      default: () => {}
    },

    actionType: {
      type: String,
      default: 'preview'
    },

    history: {
      type: Array,
      default: () => []
    },

    drawerType: {
      type: String,
      required: true
    }
  },
  components: {
    Header,
    Preview,
    Edit,
    EmptyState
  },
  computed: {
    ...mapState({
      company: ({ company }) => company.selectedCompany
    }),

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    }
  },
  data () {
    return {
      currentStatus: null,
      drawerData: {},
      contentHeight: 'none',
      action: 'preview',
      paymentMethod: null,
      paymentMethodData: {},
      confirmChangePaymentMethod: false
    }
  },
  methods: {
    hasRole (group, role) {
      return hasRole(group, role)
    },

    calcContentHeight () {
      this.contentHeight = calcContentHeight()
    },

    goBack () {
      this.$emit('goBack')
    },

    close (reload) {
      this.action = 'preview'
      this.$emit('close', reload)
    },

    change (val) {
      this.$emit('change', val)
    },

    renderCreditCardData ({ lastCardDigits, brand, id, holderName }) {
      const brands = {
        AmericanExpress: 'amex',
        International: 'international',
        JCB: 'jcb',
        Maestro: 'maestro',
        MasterCard: 'mastercard',
        Visa: 'visa'
      }

      this.paymentMethodData = {
        number: `•••• ${lastCardDigits}`,
        flag: brands[brand] || brand.toLowerCase(),
        oldCreditCard: id,
        holderName
      }
    },

    async getCreditCardData () {
      try {
        if (!this.hasRole('billing', 'ROLE_GET_FINANCIAL_ACCOUNT_PAYMENT_METHOD')) return

        const financialAccountId = this.company.financialAccountId
        const { data: res } = await this.$apiService.getCreditCardData(financialAccountId)

        if (!res.valid) {
          const msg = Array.isArray(res.reasons) && res.reasons.length
            ? res.reasons.join(', ')
            : res.message

          catchError(msg)
          throw new Error()
        }

        const { lastCardDigits, brand, id, holderName } = res.content
        this.renderCreditCardData({ lastCardDigits, brand, id, holderName })
      } catch (error) {
        catchError(error)
      }
    },

    async loadData () {
      if (this.currentStatus === STATUS_IS_LOADING) return

      this.currentStatus = STATUS_IS_LOADING

      try {
        this.paymentMethod = this.company.paymentMethod

        if (this.paymentMethod === 'CREDIT_CARD') {
          await this.getCreditCardData()
        }

        this.$emit('updateHistory', {
          drawerType: 'paymentMethod',
          data: this.data
        })

        this.currentStatus = STATUS_SUCCESS
      } catch (error) {
        catchError(error)
        this.currentStatus = STATUS_FAILED
      }
    },

    toggleAction (action) {
      this.confirmChangePaymentMethod = (action === 'editConfirm')
      this.action = action
      if (action === 'preview') this.loadData()
    }
  },
  watch: {
    data: {
      deep: true,
      handler (val) {
        this.loadData()
      }
    },

    actionType (val) {
      val && this.toggleAction(val)
    },

    company () {
      this.close()
    }
  },
  mounted () {
    this.calcContentHeight()
    window.addEventListener('resize', this.calcContentHeight)
    this.actionType && this.toggleAction(this.actionType)
    this.loadData()
  },
  beforeDestroy () {
    window.addEventListener('resize', this.calcContentHeight)
  }
}
</script>

<style lang="scss" scoped>
</style>
